/**
 * RESTORE EXPIRED DIALOG
 * Dialog de restauration manuelle d'un BPI auto-expiré (DATA-02, admin/superadmin).
 * Délègue à la RPC restore_expired_payment_case (migration 00127) : le dossier
 * repasse en "draft" (en attente de choix de paiement), le motif est tracé en
 * timeline (event payment_restored). La garde de rôle est enforcée côté serveur.
 */

import { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from '../ui';
import { usePaymentActions } from '../../hooks/usePaymentActions';
import type { PaymentCaseWithRelations } from '../../hooks/usePaymentCase';

interface RestoreExpiredDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  paymentCase: PaymentCaseWithRelations;
  prospectName: string;
  productName: string;
  onRestored: () => void;
}

export function RestoreExpiredDialog({
  open,
  onOpenChange,
  paymentCase,
  prospectName,
  productName,
  onRestored,
}: RestoreExpiredDialogProps) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { restoreExpiredPaymentCase } = usePaymentActions();

  const canSubmit = !!reason.trim() && !submitting;

  async function handleRestore() {
    if (!reason.trim()) return;

    setSubmitting(true);
    const result = await restoreExpiredPaymentCase({
      paymentCaseId: paymentCase.id,
      reason: reason.trim(),
    });

    if (result.success) {
      toast.success('Dossier ré-ouvert (remis en attente de paiement)');
      setReason('');
      onOpenChange(false);
      onRestored();
    } else {
      toast.error(result.error ?? 'Erreur lors de la ré-ouverture du dossier');
    }
    setSubmitting(false);
  }

  function handleClose(value: boolean) {
    if (!submitting) {
      setReason('');
      onOpenChange(value);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>Ré-ouvrir le dossier expiré</DialogTitle>
          <DialogDescription>
            Prospect <strong>{prospectName}</strong> — BPI {productName} —{' '}
            <span className="font-mono text-xs">{paymentCase.order_id || '—'}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Le dossier repassera en attente de choix de paiement. Le numéro d'équipage libéré à
            l'expiration n'est pas restauré : une nouvelle allocation se fera au prochain paiement.
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-zinc-700">
              Motif <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Le prospect a relancé par téléphone, souhaite finaliser son inscription."
              rows={4}
              disabled={submitting}
            />
            <p className="text-xs text-zinc-400">
              Ce motif sera enregistré dans la timeline du dossier.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={submitting}>
            Annuler
          </Button>
          <Button
            onClick={handleRestore}
            disabled={!canSubmit}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {submitting ? 'Ré-ouverture...' : 'Ré-ouvrir le dossier'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
